import { useCallback } from 'react';

import { useRouter } from 'expo-router';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { appendObjectToQueryString, convertQueryStringToObject } from 'navigation/utils';
import { useRedeemReward } from 'pages/cart/cart-item/hooks/use-redeem-reward-state';
import { CustomEventNames, logRBIProductsDetailsEvent } from 'state/amplitude';
import { booleanToString } from 'state/amplitude/utils';
import { useCartContext } from 'state/cart';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { RewardRedemptionMethod } from 'state/loyalty/hooks/types';
import { useGetAvailableRewards } from 'state/loyalty/hooks/use-get-available-rewards';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { isCartEntryPicker } from 'utils/cart/is-cart-type';
import { routes } from 'utils/routing';

import { useRoute } from './navigation/use-route';
import { useCart } from './use-cart';

enum IncrementorActions {
  Increment = 'increment',
  Decrement = 'decrement',
}

export const useCartItemEditingTools = ({
  item,
  isOffer = false,
}: {
  item: ILegacyCartEntry;
  isOffer?: boolean;
}) => {
  const { confirmRemoveItemFromCart } = useCartContext();
  const { updateQuantity } = useCart();
  const router = useRouter();
  const { pathname } = useRoute();
  const { getAvailableRewardFromCartEntry } = useGetAvailableRewards();
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { loyaltyUser } = useLoyaltyUser();
  const { incentiveNotInMenu } = useRedeemReward(item);
  const dispatch = useAppDispatch();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);

  const handleEditItem = () => {
    // If its an extra take them to the extras modal
    if (item.isExtra) {
      return router.push(routes.cartAddExtras);
    }

    // If its a reorder item always take directly to the item.
    // Only allow picker changes if we haven't ordered it before.
    const baseUrl = item.reorder
      ? `/menu/${item.type.toLowerCase()}-${item._id}`
      : item.url.split('?')[0];

    // This is to support offer pickers editing
    const queryParams = convertQueryStringToObject<{ offerId?: string }>(
      item.url.replace(`${baseUrl}?`, '')
    );
    const offerIdParam = queryParams.offerId;

    const appliedReward = appliedLoyaltyRewards[item.cartId];
    const productWizardEditUrl = appendObjectToQueryString(baseUrl, {
      isEdit: true,
      cartIdEditing: item.cartId,
      isFromCart: pathname.startsWith(routes.cart),
      isProductReviewFromCart: isCartEntryPicker(item.url),
      ...(appliedReward
        ? { rewardBenefitId: appliedReward.rewardBenefitId, rewardId: appliedReward.sanityId }
        : {}),
      ...(offerIdParam ? { offerId: offerIdParam } : {}),
    });

    const attributes = {
      'Product Id': item._id,
      'Product Name': item.name,
      'Edit Url': productWizardEditUrl,
      'Is Reorder': booleanToString(item?.reorder ?? false),
      'Is Offer': booleanToString(isOffer),
    };
    logRBIProductsDetailsEvent({
      name: CustomEventNames.EDIT_CART,
      products: [item],
      attributes,
    });

    router.push(productWizardEditUrl);
  };

  const handleRemoveItem = useCallback(() => confirmRemoveItemFromCart(item.cartId), [
    item,
    confirmRemoveItemFromCart,
  ]);

  const handleQuantityChange = useCallback(
    (quantity: number, incrementorAction: IncrementorActions) => {
      if (loyaltyEnabled) {
        const { rewardBenefitId } = getAvailableRewardFromCartEntry(item) || {};
        const numberOfAppliedRewards = appliedLoyaltyRewards[item.cartId]?.timesApplied;
        const quantityLessThanAppliedRewards = quantity < numberOfAppliedRewards;
        const { applyReward, unApplyReward } = actions.loyalty;
        if (quantityLessThanAppliedRewards && rewardBenefitId) {
          dispatch(
            unApplyReward({
              rewardBenefitId,
              cartId: item.cartId,
              loyaltyUser,
            })
          );
        }

        // if incentive is not in menu, any quantity update should apply or remove the incentive
        if (
          incentiveNotInMenu &&
          incrementorAction === IncrementorActions.Increment &&
          rewardBenefitId
        ) {
          dispatch(
            applyReward({
              rewardBenefitId,
              cartId: item.cartId,
              redemptionMethod: RewardRedemptionMethod.MOBILE,
              enableRewardUnstacking,
            })
          );
        }
      }

      updateQuantity(item.cartId, quantity);
    },
    [
      appliedLoyaltyRewards,
      dispatch,
      getAvailableRewardFromCartEntry,
      incentiveNotInMenu,
      item,
      loyaltyEnabled,
      loyaltyUser,
      updateQuantity,
      enableRewardUnstacking,
    ]
  );

  return {
    handleEditItem,
    handleRemoveItem,
    handleQuantityChange,
  };
};
